import { Component, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, finalize, switchMap, debounceTime, distinctUntilChanged } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import {
  TabHeaderComponent,
  TabItem,
} from '../../../shared/components/tab-header/tab-header.component';
import { SearchbarComponent } from '../../../shared/components/searchbar/searchbar.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import { RewardsService } from '../../../services/rewards.service';
import {
  AdminRewardsSummaryFilters,
  AdminRewardsSummaryItem,
  RewardRule,
  RewardsHistoryColumn,
} from '../../../rewards/models/rewards-v2.models';
import { RewardEarningCriteriaComponent } from './components/reward-earning-criteria/reward-earning-criteria.component';
import { RewardRuleModalComponent } from './components/reward-rule-modal/reward-rule-modal.component';
import { RewardsSummaryTabComponent } from './components/rewards-summary-tab/rewards-summary-tab.component';
import { RewardsListTabComponent } from './components/rewards-list-tab/rewards-list-tab.component';
import { getRewardSummaryDateRange } from './utils/rewards-summary-period.util';

const SUB_TABS: TabItem[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'rewards-list', label: 'Rewards List' },
];

const SUMMARY_PERIOD_OPTIONS: PeriodOption[] = [
  { label: 'Today', value: 'Today' },
  { label: 'This Week', value: 'This Week' },
  { label: 'This Bi-Week', value: 'This Bi-Week' },
  { label: 'This Month', value: 'This Month' },
  { label: 'This Year', value: 'This Year' },
  { label: 'All Time', value: 'All Time' },
  { label: 'Custom Period', value: 'Custom' },
];

@Component({
  selector: 'app-rewards-tab-v2',
  standalone: true,
  imports: [
    CommonModule,
    TabHeaderComponent,
    SearchbarComponent,
    DateRangePickerComponent,
    RewardEarningCriteriaComponent,
    RewardRuleModalComponent,
    PaginationBarComponent,
    RewardsSummaryTabComponent,
    RewardsListTabComponent,
  ],
  providers: [NotificationService],
  templateUrl: './rewards-tab-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-tab-v2.component.scss'],
})
export class RewardsTabV2Component implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly summaryLoad$ = new Subject<AdminRewardsSummaryFilters>();
  private readonly summarySearch$ = new Subject<string>();

  readonly subTabs = SUB_TABS;
  readonly summaryPeriodOptions = SUMMARY_PERIOD_OPTIONS;
  readonly summaryItemsPerPageOptions = [10, 25, 50, 100];

  activeSubTab = 'summary';
  rewardRules: RewardRule[] = [];
  activeRewardRules: RewardRule[] = [];
  isLoadingRules = false;
  showRewardRuleModal = false;
  editingRule: RewardRule | null = null;

  summarySearchTerm = '';
  selectedSummaryPeriod = 'Today';
  isSummaryDateRangePickerOpen = false;
  summaryCustomDateRange: DateRange = { startDate: null, endDate: null };

  isLoadingSummary = false;
  summaryColumns: RewardsHistoryColumn[] = [];
  summaryItems: AdminRewardsSummaryItem[] = [];
  summaryPaginationData: PaginationData = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  };

  constructor(
    private readonly rewardsService: RewardsService,
    private readonly messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.summarySearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => {
        this.summarySearchTerm = term;
        if (this.activeSubTab === 'summary') {
          this.loadSummary(1);
        }
      });

    this.summaryLoad$
      .pipe(
        switchMap(filters => {
          this.isLoadingSummary = true;
          return this.rewardsService.getAdminRewardsEarningSummary(filters).pipe(
            finalize(() => {
              this.isLoadingSummary = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: data => {
          this.summaryColumns = data.columns ?? [];
          this.summaryItems = data.items ?? [];
          const pagination = data.pagination ?? {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
          };
          this.summaryPaginationData = {
            currentPage: pagination.page,
            totalPages: pagination.totalPages > 0 ? pagination.totalPages : 1,
            totalItems: pagination.total,
            itemsPerPage: pagination.limit,
          };
        },
        error: () => {
          this.summaryColumns = [];
          this.summaryItems = [];
          this.summaryPaginationData = {
            ...this.summaryPaginationData,
            totalItems: 0,
            totalPages: 1,
          };
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load rewards summary. Please try again.',
            life: 5000,
          });
        },
      });

    this.loadRewardRules();
    this.loadSummary(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get summaryFiltersDisabled(): boolean {
    return this.activeSubTab !== 'summary';
  }

  get showSummaryPagination(): boolean {
    return !this.isLoadingSummary && this.summaryItems.length > 0;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isSummaryDateRangePickerOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    const dropdownWrapper = target.closest('.period-dropdown-wrapper');
    const allCalendarPanels = document.querySelectorAll('.p-datepicker-panel');
    let isInsideCalendarPanel = false;
    allCalendarPanels.forEach(panel => {
      if (panel.contains(target)) {
        isInsideCalendarPanel = true;
      }
    });
    if (!dropdownWrapper && !isInsideCalendarPanel) {
      this.isSummaryDateRangePickerOpen = false;
    }
  }

  refreshRewardRules(): void {
    this.loadRewardRules();
  }

  openRewardRuleModal(): void {
    this.editingRule = null;
    this.showRewardRuleModal = true;
  }

  openRewardRuleModalForEdit(rule: RewardRule): void {
    this.editingRule = rule;
    this.showRewardRuleModal = true;
  }

  closeRewardRuleModal(): void {
    this.showRewardRuleModal = false;
    this.editingRule = null;
  }

  onRuleCreated(): void {
    this.refreshRewardRules();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Reward rule created successfully',
      life: 5000,
    });
    if (this.activeSubTab === 'summary') {
      this.loadSummary(this.summaryPaginationData.currentPage);
    }
  }

  onRuleUpdated(): void {
    this.refreshRewardRules();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Reward updated successfully',
      life: 5000,
    });
    if (this.activeSubTab === 'summary') {
      this.loadSummary(this.summaryPaginationData.currentPage);
    }
  }

  onRuleDisabled(): void {
    this.refreshRewardRules();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Reward disabled successfully',
      life: 5000,
    });
    if (this.activeSubTab === 'summary') {
      this.loadSummary(this.summaryPaginationData.currentPage);
    }
  }

  onSubTabChange(tabId: string): void {
    this.activeSubTab = tabId;
    if (tabId === 'summary') {
      this.loadSummary(this.summaryPaginationData.currentPage);
    }
  }

  onSummarySearchChange(searchTerm?: string): void {
    if (this.summaryFiltersDisabled) {
      return;
    }
    this.summarySearch$.next(searchTerm ?? '');
  }

  toggleSummaryDateRangePicker(): void {
    if (this.summaryFiltersDisabled) {
      return;
    }
    this.isSummaryDateRangePickerOpen = !this.isSummaryDateRangePickerOpen;
  }

  closeSummaryDateRangePicker(): void {
    this.isSummaryDateRangePickerOpen = false;
  }

  onSummaryDateRangePeriodChange(period: string): void {
    this.selectedSummaryPeriod = period;
  }

  onSummaryCustomDateRangeChange(dateRange: DateRange): void {
    this.summaryCustomDateRange = dateRange;
  }

  onSummaryDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    if (this.summaryFiltersDisabled) {
      return;
    }
    this.selectedSummaryPeriod = data.period;
    if (data.customRange) {
      this.summaryCustomDateRange = data.customRange;
    }
    this.isSummaryDateRangePickerOpen = false;
    this.loadSummary(1);
  }

  onSummaryPageChange(page: number): void {
    this.loadSummary(page);
  }

  onSummaryItemsPerPageChange(itemsPerPage: number): void {
    this.summaryPaginationData = {
      ...this.summaryPaginationData,
      itemsPerPage,
    };
    this.loadSummary(1);
  }

  private loadSummary(page: number): void {
    if (this.activeSubTab !== 'summary') {
      return;
    }

    const dateRange = getRewardSummaryDateRange(
      this.selectedSummaryPeriod,
      this.summaryCustomDateRange
    );
    const search = this.summarySearchTerm.trim();

    const filters: AdminRewardsSummaryFilters = {
      page,
      limit: this.summaryPaginationData.itemsPerPage,
    };
    if (dateRange) {
      filters.start_date = dateRange.startDate;
      filters.end_date = dateRange.endDate;
    }
    if (search) {
      filters.search = search;
    }

    this.summaryPaginationData = {
      ...this.summaryPaginationData,
      currentPage: page,
    };
    this.summaryLoad$.next(filters);
  }

  private loadRewardRules(): void {
    this.isLoadingRules = true;

    this.rewardsService
      .getRewardRules()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingRules = false;
        })
      )
      .subscribe({
        next: (rules: RewardRule[]) => {
          this.rewardRules = rules;
          this.activeRewardRules = rules.filter(rule => rule.is_active);
        },
        error: () => {
          this.rewardRules = [];
          this.activeRewardRules = [];
        },
      });
  }
}
